'use client';

import { useEffect, useRef, useState } from 'react';

import { clpp } from '@castlabs/prestoplay';
import '@castlabs/prestoplay/cl.mse';
import '@castlabs/prestoplay/cl.dash';
import '@castlabs/prestoplay/cl.hls';
import '@castlabs/prestoplay/cl.htmlcue';
import '@castlabs/prestoplay/cl.vtt';
import '@castlabs/prestoplay/clpp.styles.css';
import { useUserData } from '@/context/auth/user-data-context';

type MuxJsModule = typeof import('mux.js');

type WindowWithMux = Window & {
    muxjs?: MuxJsModule;
};

interface VideoPlayerProps {
    src: string;
    subtitles?: string;
}

function VideoPlayer({ src, subtitles }: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<clpp.Player | null>(null);
    const { loading, isAdmin, id } = useUserData();

    useEffect(() => {
        const video = videoRef.current;
        if (!video || loading) return;

        (async () => {
            const win = window as WindowWithMux;
            if (!win.muxjs) {
                try {
                    const mux = await import('mux.js');
                    win.muxjs = mux;
                } catch {
                    // Do nothing
                }
            }

            if (!playerRef.current) {
                clpp.install(clpp.hls.HlsComponent);
                clpp.install(clpp.dash.DashComponent);
                clpp.install(clpp.htmlcue.HtmlCueComponent);
                clpp.install(clpp.vtt.VttComponent);

                const onLoadedMeta = () => {
                    const w = video.videoWidth || 16;
                    const h = video.videoHeight || 9;
                    const container = video.closest('.clpp-container') as HTMLElement | null;
                    if (h > 0 && container) {
                        container.style.aspectRatio = `${w} / ${h}`;
                    }
                };
                video.addEventListener('loadedmetadata', onLoadedMeta, { once: true });

                const player = new clpp.Player(video, {
                    license:
                        'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmxzIjpbImh0dHBzOi8vYnVucWVyLnRlY2giXSwidHlwZSI6IldlYiIsImtpZCI6NDM1MCwiaWx2IjpmYWxzZX0.X21-C33cBII0BsqgCvVVkYi-jhUQFtVCJUxxYYJA7v9kDJY5hLgbQizwt90fGYtsPU6GAnaqMn4CwNcZg8IscAzZh9GrSpsiwxUf9Ezzl0Xfe261xjdEfn7UwWm1U9sQeCj6wwJ4qJPF2EHALMAWR8r4Nu2njmKbZj57aOQ2K60wf0ICt3J26fWfqAFd3QChXtDZRaDhOqlAIrTsbX98mvo9CsHnUiFnrbiJ3CBj0SE15cwDS0hQ6qOjE8FgS4CdWlek0SEdtHZjSQ5CTkdyZCtvmqXig51R0ckyNI4Csd0pH0m-yPPRh7N_2Dd8oUGzNy2hsuB0hzzGMkM-ZHsd3g',
                    viewerId: isAdmin ? 'admin' : (id as string),
                });
                player.use(clpp.hls.HlsComponent);
                playerRef.current = player;
            }

            const config: clpp.PlayerConfiguration = {
                source: src,
                autoplay: true,
                preferredTextLanguage: 'en',
            };

            if (subtitles) {
                config.hlssmpte = {
                    patchMetadataTrack: true,
                };
                config.htmlcue = {
                    stretchSmpteImage: true,
                };

                config.remoteTextTracks = [
                    {
                        url: subtitles,
                        kind: 'subtitles',
                        language: 'en',
                        mimeType: 'text/srt',
                        label: 'English',
                    },
                ];

                config.enableHtmlCue = false;
            }

            playerRef.current.load({
                ...config,
            });
        })();
    }, [src, subtitles, loading, id, isAdmin]);

    return (
        <div className="rounded-[12px] overflow-hidden">
            {/* biome-ignore lint/a11y/useMediaCaption: Captions managed by PrestoPlay */}
            <video ref={videoRef} controls crossOrigin="anonymous" />
        </div>
    );
}

export default VideoPlayer;
